import type { RequestOptions } from "@@/plugin-request/request";
import { request } from "@umijs/max";

/**
 * 统一请求封装（企业级）
 *
 * 对 @umijs/max 的 request 做二次封装，简化调用方式：
 * - http.get<T>(url, params, options)
 * - http.post<T>(url, data, options)
 * - http.put<T>(url, data, options)
 * - http.del<T>(url, params, options)
 * - http.patch<T>(url, data, options)
 * - http.download(url, params, filename)
 * - http.upload<T>(url, file, params)
 *
 * 特性：
 * 1. BASE_URL 可通过环境变量覆盖
 * 2. 自动映射分页参数 current → pageNum
 * 3. 文件下载自动处理 blob
 * 4. 文件上传自动设置 FormData + Content-Type
 * 5. 支持 AbortController 取消请求
 * 6. 支持 silent 模式静默错误
 */

// ============================================================
// 配置
// ============================================================

/**
 * 接口基础路径
 * 优先读取环境变量 API_BASE_URL，默认 /api（与 proxy.ts 对应）
 */
const BASE_URL: string = process.env.API_BASE_URL || "/api";

/**
 * 分页参数映射配置
 * key = 前端参数名，value = 后端参数名
 * 扩展时只需往这里加映射，比如 { current: 'pageNum', pageSize: 'size' }
 */
const PAGE_PARAM_MAP: Record<string, string> = {
  current: "pageNum",
};

// ============================================================
// 内部工具函数
// ============================================================

/**
 * 分页参数映射
 * 按配置表把前端参数名转为后端参数名，映射后删除原 key
 */
function mapPageParams(params?: Record<string, any>): Record<string, any> {
  if (!params) return {};
  const result = { ...params };
  for (const [from, to] of Object.entries(PAGE_PARAM_MAP)) {
    if (result[from] !== undefined) {
      // 后端参数名和前端不同时才映射
      if (from !== to) {
        result[to] = result[from];
        delete result[from];
      }
    }
  }
  return result;
}

/**
 * 构建完整 URL
 */
function buildUrl(path: string): string {
  // path 已以 / 开头，直接拼接
  return `${BASE_URL}${path}`;
}

// ============================================================
// 请求选项扩展
// ============================================================

/** http 封装层的扩展选项（在 umi RequestOptions 基础上增加） */
export interface HttpOptions extends Partial<RequestOptions> {
  /** 静默模式：业务错误时不弹 message，由页面自行处理 */
  silent?: boolean;
}

/**
 * 把 HttpOptions 拆为 umi RequestOptions + 额外标记
 * silent 模式通过 skipErrorHandler 实现
 */
function normalizeOptions(options?: HttpOptions): Partial<RequestOptions> {
  if (!options) return {};
  const { silent, ...rest } = options;
  if (silent) {
    rest.skipErrorHandler = true;
  }
  return rest;
}

// ============================================================
// 请求方法
// ============================================================

const http = {
  /**
   * GET 请求
   * @param url     接口路径（如 /system/user/page），自动拼 /api
   * @param params  查询参数，自动映射分页参数
   * @param options 扩展选项（silent 静默 / skipErrorHandler 等）
   */
  get<T = any>(
    url: string,
    params?: Record<string, any>,
    options?: HttpOptions
  ) {
    return request<API.ApiResult<T>>(buildUrl(url), {
      method: "GET",
      params: mapPageParams(params),
      ...normalizeOptions(options),
    });
  },

  /**
   * POST 请求
   * @param url     接口路径
   * @param data    请求体
   * @param options 扩展选项
   */
  post<T = any>(
    url: string,
    data?: Record<string, any>,
    options?: HttpOptions
  ) {
    return request<API.ApiResult<T>>(buildUrl(url), {
      method: "POST",
      data,
      ...normalizeOptions(options),
    });
  },

  /**
   * PUT 请求
   * @param url     接口路径
   * @param data    请求体
   * @param options 扩展选项（可通过 params 传查询参数）
   */
  put<T = any>(url: string, data?: Record<string, any>, options?: HttpOptions) {
    return request<API.ApiResult<T>>(buildUrl(url), {
      method: "PUT",
      data,
      ...normalizeOptions(options),
    });
  },

  /**
   * PATCH 请求
   * @param url     接口路径
   * @param data    请求体
   * @param options 扩展选项
   */
  patch<T = any>(
    url: string,
    data?: Record<string, any>,
    options?: HttpOptions
  ) {
    return request<API.ApiResult<T>>(buildUrl(url), {
      method: "PATCH",
      data,
      ...normalizeOptions(options),
    });
  },

  /**
   * DELETE 请求
   * @param url     接口路径（如 /system/user/1）
   * @param params  查询参数
   * @param options 扩展选项（可通过 data 传请求体，如批量删除）
   */
  del<T = any>(
    url: string,
    params?: Record<string, any>,
    options?: HttpOptions
  ) {
    return request<API.ApiResult<T>>(buildUrl(url), {
      method: "DELETE",
      params,
      ...normalizeOptions(options),
    });
  },

  /**
   * 文件下载
   * 自动处理 blob 响应，触发浏览器下载
   * @param url      接口路径
   * @param params   查询参数
   * @param filename 下载文件名（不含后缀），后端通过 Content-Disposition 指定时不传
   */
  async download(url: string, params?: Record<string, any>, filename?: string) {
    const resp = await request<Blob>(buildUrl(url), {
      method: "GET",
      params,
      responseType: "blob",
      getResponse: false,
    });
    // 创建下载链接
    const blob = resp instanceof Blob ? resp : new Blob([resp as any]);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    // 优先用参数指定的文件名，否则用时间戳
    link.download = filename || `下载文件_${Date.now()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  },

  /**
   * 文件上传
   * 自动构建 FormData，设置 multipart/form-data
   * @param url     接口路径
   * @param file    上传的文件（File 对象）
   * @param params  额外参数（如 { module: 'avatar' }）
   * @param options 扩展选项
   */
  upload<T = any>(
    url: string,
    file: File,
    params?: Record<string, any>,
    options?: HttpOptions
  ) {
    const formData = new FormData();
    formData.append("file", file);
    // 额外参数拼入 FormData
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    return request<API.ApiResult<T>>(buildUrl(url), {
      method: "POST",
      data: formData,
      // 不设 Content-Type，让浏览器自动加 multipart boundary
      ...normalizeOptions(options),
    });
  },

  /**
   * 创建 AbortController，用于取消请求
   * 用法：const ctrl = http.createAbort(); http.get('/url', {}, { signal: ctrl.signal }); ctrl.abort();
   */
  createAbort(): AbortController {
    return new AbortController();
  },
};

export default http;
